import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import "@/App.css";
import axios from "axios";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Toaster as SonnerToaster } from "sonner";
import apiClient, { 
  getToken, setToken, removeToken,
  getStoredUser, setStoredUser, clearAuth,
  fetchCurrentUser, API
} from "./config/api";

// Lazy load the main Dashboard layout
const Dashboard = lazy(() => import("./components/layout/Dashboard").then(m => ({ default: m.Dashboard })));

// Lazy load pages for admin access
const FactoryFloorPage = lazy(() => import("./pages/FactoryFloorPage").then(m => ({ default: m.FactoryFloorPage })));
const AdminPanel = lazy(() => import("./pages/AdminPanel").then(m => ({ default: m.AdminPanel })));

// Lazy load auth pages
const LandingPage = lazy(() => import("./pages/LandingPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const LoginPage = lazy(() => import("./pages/LoginPage").then(m => ({ default: m.LoginPage })));
const SitePage = lazy(() => import("./pages/SitePage"));
const ZapApp = lazy(() => import("./zap/ZapApp"));

// Import modals and prompts
import { InstallPWAPrompt } from "./components/modals/InstallPWAPrompt";
// UpdateAvailableModal removido — notificação de update agora aparece no sino 🔔 (NotificationBell)
import { UpdatePrompt } from "./components/UpdatePrompt";
import { ProUpgradePromotion } from "./components/promo/ProComponents";
import { OfflineIndicator } from "./components/common/OfflineStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-white">Carregando...</div>
  </div>
);

// ==================== MAIN APP ====================
function App() {
  const [currentPage, setCurrentPage] = useState("landing");
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInstallPrompt, setShowInstallPrompt] = useState(true);
  
  // Version modal - desabilitado pois já existe no index.html
  // O index.html já gerencia a exibição do modal de versão usando 'craftflow_version_updated'
  const [showVersionModal, setShowVersionModal] = useState(false);
  
  const handleCloseVersionModal = () => {
    setShowVersionModal(false);
  };

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) return;
    
    const poll = async (id, count) => {
      try {
        const response = await apiClient.get(`/payments/status/${id}`);
        if (response.data.status === 'paid') {
          // Refresh user data using JWT
          const updatedUser = await fetchCurrentUser();
          if (updatedUser) {
            setUser(updatedUser);
          }
          alert('🎉 Pagamento confirmado! Seu plano Pro foi ativado.');
        } else if (response.data.status === 'pending' && count < maxAttempts) {
          setTimeout(() => poll(id, count + 1), 2000);
        }
      } catch (err) {
        console.error('Error polling payment:', err);
      }
    };
    
    await poll(sessionId, attempts);
  }, []);

  // Check for payment callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      pollPaymentStatus(sessionId);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [pollPaymentStatus]);

  // Initialize authentication on app load
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      
      // Check for existing token
      const token = getToken();
      const storedUser = getStoredUser();
      
      if (token && storedUser) {
        // Verify token is still valid by fetching user data
        try {
          const freshUser = await fetchCurrentUser();
          if (freshUser) {
            setUser(freshUser);
            setCurrentPage("dashboard");
          } else {
            // Token invalid, clear auth
            clearAuth();
          }
        } catch (error) {
          console.log('Token validation failed:', error);
          clearAuth();
        }
      }
      
      setIsLoading(false);
      
      // Check if accessing factory floor page
      if (window.location.pathname === '/fabrica' || 
          window.location.pathname === '/chao-de-fabrica' || 
          window.location.hash === '#fabrica') {
        setCurrentPage("fabrica");
      }

      // Rota pública /site — landing comercial editada pelo admin
      if (window.location.pathname === '/site' || window.location.hash === '#site') {
        setCurrentPage("site");
      }
    };
    
    initAuth();
    
    // Listen for auth:logout events from API interceptor
    const handleLogoutEvent = () => {
      setUser(null);
      setCurrentPage("landing");
    };
    
    window.addEventListener('auth:logout', handleLogoutEvent);
    return () => window.removeEventListener('auth:logout', handleLogoutEvent);
  }, []);

  const handleLogout = () => {
    clearAuth();
    setUser(null);
    setCurrentPage("landing");
  };

  const handleLogin = (loginResponse) => {
    // loginResponse now includes access_token
    const { user: userData, access_token } = loginResponse;
    
    if (access_token) {
      setToken(access_token);
    }
    
    setStoredUser(userData);
    setUser(userData);
    setCurrentPage("dashboard");
  };

  // Show loading while checking auth
  if (isLoading) {
    return <LoadingFallback />;
  }

  // Factory Floor Page - Independent access for workers
  if (currentPage === "fabrica") {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <FactoryFloorPage />
      </Suspense>
    );
  }

  // Site comercial público (rota /site) — landing dinâmica editada pelo admin
  if (currentPage === "site") {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <SitePage
          onEnter={() => { window.history.pushState({}, '', '/'); setCurrentPage(user ? "dashboard" : "login"); }}
          onRegister={() => { window.history.pushState({}, '', '/'); setCurrentPage("register"); }}
        />
      </Suspense>
    );
  }

  // Landing Page (not logged in)
  if (!user && currentPage === "landing") {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <LandingPage 
          onShowRegister={() => setCurrentPage("register")} 
          onShowLogin={() => setCurrentPage("login")} 
          onShowFactoryFloor={() => setCurrentPage("fabrica")}
        />
        {showInstallPrompt && <InstallPWAPrompt onClose={() => setShowInstallPrompt(false)} />}
      </Suspense>
    );
  }

  // Register Page
  if (!user && currentPage === "register") {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <RegisterPage onSuccess={handleLogin} onBack={() => setCurrentPage("landing")} />
      </Suspense>
    );
  }

  // Login Page
  if (!user && currentPage === "login") {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <LoginPage onSuccess={handleLogin} onBack={() => setCurrentPage("landing")} />
      </Suspense>
    );
  }

  // Logged in user
  if (user) {
    // Admin Panel
    if (currentPage === "admin" && user.role === "admin") {
      return (
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <AdminPanel user={user} setCurrentPage={setCurrentPage} onLogout={handleLogout} />
            {showInstallPrompt && <InstallPWAPrompt onClose={() => setShowInstallPrompt(false)} />}
          </Suspense>
        </ErrorBoundary>
      );
    }
    
    // Main Dashboard (all other pages)
    return (
      <>
        <Suspense fallback={<LoadingFallback />}>
          <Dashboard 
            user={user} 
            currentPage={currentPage} 
            setCurrentPage={setCurrentPage} 
            onLogout={handleLogout}
            setUser={setUser}
          />
        </Suspense>
        {showInstallPrompt && <InstallPWAPrompt onClose={() => setShowInstallPrompt(false)} />}
        <ProUpgradePromotion 
          user={user} 
          onUpgrade={() => setCurrentPage("upgrade")}
        />
        {/* Indicador de Offline */}
        <OfflineIndicator />
      </>
    );
  }

  return null;
}

function AppWrapper() {
  const isZap = window.location.pathname.startsWith('/zap');
  return (
    <>
      <UpdatePrompt />
      {isZap ? <Suspense fallback={<LoadingFallback />}><ZapApp /></Suspense> : <App />}
      <SonnerToaster 
        position="top-center"
        richColors
        theme="dark"
        toastOptions={{
          style: { background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }
        }}
      />
    </>
  );
}

export default AppWrapper;
